const SESSION_KEY_VERIFIER = 'oauth_code_verifier';
const SESSION_KEY_STATE = 'oauth_state';
const SESSION_KEY_RETURN_URL = 'oauth_return_url';

function base64urlEncode(buffer: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/** Генерирует криптографически случайный code_verifier (43–128 символов, URL-safe). */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(48);
  crypto.getRandomValues(array);
  return base64urlEncode(array.buffer);
}

/** Вычисляет code_challenge = BASE64URL(SHA256(code_verifier)). */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  if (!crypto.subtle) {
    throw new Error(
      'Web Crypto API недоступна. OAuth требует secure context (HTTPS или localhost). ' +
        'Откройте приложение по https://localhost:3000 или http://localhost:3000.',
    );
  }
  const encoded = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', encoded);
  return base64urlEncode(hash);
}

/** Генерирует случайный state для защиты от CSRF. */
export function generateState(): string {
  const array = new Uint8Array(24);
  crypto.getRandomValues(array);
  return base64urlEncode(array.buffer);
}

/** Сохраняет PKCE-данные и return URL в sessionStorage перед редиректом. */
export function savePKCESession(verifier: string, state: string, returnUrl: string): void {
  sessionStorage.setItem(SESSION_KEY_VERIFIER, verifier);
  sessionStorage.setItem(SESSION_KEY_STATE, state);
  sessionStorage.setItem(SESSION_KEY_RETURN_URL, returnUrl);
}

/** Читает PKCE-данные без удаления (для callback до успешного обмена кода). */
export function peekPKCESession(): {
  verifier: string;
  state: string;
  returnUrl: string;
} | null {
  const verifier = sessionStorage.getItem(SESSION_KEY_VERIFIER);
  const state = sessionStorage.getItem(SESSION_KEY_STATE);
  const returnUrl = sessionStorage.getItem(SESSION_KEY_RETURN_URL);

  if (!verifier || !state || !returnUrl) {
    return null;
  }

  return { verifier, state, returnUrl };
}

/** Удаляет PKCE-данные из sessionStorage после успеха/ошибки обмена кода. */
export function clearPKCESession(): void {
  sessionStorage.removeItem(SESSION_KEY_VERIFIER);
  sessionStorage.removeItem(SESSION_KEY_STATE);
  sessionStorage.removeItem(SESSION_KEY_RETURN_URL);
}

/** Читает и удаляет PKCE-данные одним шагом (например, откат к старому потоку). */
export function consumePKCESession(): {
  verifier: string;
  state: string;
  returnUrl: string;
} | null {
  const session = peekPKCESession();
  if (session) {
    clearPKCESession();
  }
  return session;
}
